import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Field,
  Label,
  Option,
  useId,
} from '@fluentui/react-components';
import { maxKeywordCount } from '../../constants/maximums';
import { errorMessages } from '../../errors';
import { useMeasurementTypeSelectorStyles } from '../../styles';
import { RequiredDropdown } from '../validated/required-dropdown';
import { KeywordDisplay } from './keyword-display';

export const KeywordSelector = ({
  fetchedKeywords,
  isDisabled,
  onRemove,
  onRemoveAll,
  onSelect,
  orderedFetchedKeywords,
  selected,
  showError,
  useSearchLift,
}) => {
  const dropdownId = useId('useKeywordSelector');
  const styles = useMeasurementTypeSelectorStyles();
  const i18n = useContext(I18nContext);
  const placeholder = i18n.getString(_TL_('Select keywords'));
  const impressionsUnit = i18n.getString(_TL_('impressions'));

  const buttonText = useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    return selected.map(keywordId => fetchedKeywords[keywordId].name).join(i18n.getString(_TL_(', ')));
  }, [fetchedKeywords, i18n, placeholder, selected]);

  const onSelectDropdownOption = useCallback((e, data) => {
    onSelect(data.selectedOptions);
  }, [onSelect]);

  const [fieldProps, setFieldProps] = useState(null);

  const onValidateKeywords = useCallback(({ isValid, messages }) => {
    if (isValid) {
      setFieldProps(null);
    } else {
      setFieldProps({
        validationMessage: i18n.getString(errorMessages[messages[0]]),
        validationState: 'error',
      });
    }
  }, [i18n]);

  return (
    <div className={styles.dropdownContainer}>
      <Label
        disabled={isDisabled}
        id={dropdownId}
        required
      >
        {i18n.getString(_TL_('Keywords'))}
      </Label>
      {selected.length > 0 && (
        <KeywordDisplay
          fetchedKeywords={fetchedKeywords}
          onRemoveAllKeywords={onRemoveAll}
          onRemoveKeyword={onRemove}
          selectedKeywords={selected}
        />
      )}
      <Field {...(showError ? fieldProps : null)}>
        <RequiredDropdown
          aria-labelledby={dropdownId}
          button={<span className={styles.truncatedText}>{buttonText}</span>}
          className={styles.dropdown}
          disabled={isDisabled}
          errorMessage="SELECTED_KEYWORDS_REQUIRED"
          multiselect
          onOptionSelect={onSelectDropdownOption}
          useSearchLift={useSearchLift}
          validationProps={{
            data: { selectedOptions: selected },
            field: 'selectedOptions',
            onValidate: onValidateKeywords,
          }}
        >
          {orderedFetchedKeywords.map(({ id, impressions, name }) => {
            const isMaxedOut = selected.length === maxKeywordCount;
            const isOptionDisabled = isMaxedOut && !selected.includes(id);
            const optionLabel = `${name} (${impressions} ${impressionsUnit})`;

            return (
              <Option
                className={`keyword-selector-option-${name}`}
                disabled={isOptionDisabled}
                key={id}
                value={id}
              >
                {optionLabel}
              </Option>
            );
          })}
        </RequiredDropdown>
      </Field>
    </div>
  );
};

KeywordSelector.propTypes = {
  fetchedKeywords: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    impressions: PropTypes.number.isRequired,
  })),
  isDisabled: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRemoveAll: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  orderedFetchedKeywords: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    impressions: PropTypes.number.isRequired,
  })),
  selected: PropTypes.arrayOf(PropTypes.number).isRequired,
  showError: PropTypes.bool,
  useSearchLift: PropTypes.bool.isRequired,
};

KeywordSelector.defaultProps = {
  fetchedKeywords: null,
  orderedFetchedKeywords: null,
  showError: false,
};
